// Consumer tenant portal — sidebar nav model.
//
// Mirrors design/UI : UX v1.0/for-engineering-tenant-shell/specs/
// tenant-shell-strings.md § "The 5 nav items".
//
// V1: 5 items, Coach topmost (signals AI coaching is the headline).
// V1.x: tenant_member role hides Adoption (designed in mock but not
//       gate-blocked for V1 demo).
//
// Labels live in i18n strings (shell.* namespace) — looked up by key.

import type { ConsumerRoleKey } from '@gc-coach/shared';

export type NavItemKey =
  | 'coach'
  | 'dashboard'
  | 'frameworks'
  | 'notifications'
  | 'adoption';

export interface NavItem {
  key: NavItemKey;
  /** Route this nav item navigates to. */
  to: string;
  /**
   * Roles that may see this item.
   * `null` = visible to every signed-in tenant user.
   */
  requiredRoles: ReadonlyArray<ConsumerRoleKey> | null;
}

export const NAV_MODEL: ReadonlyArray<NavItem> = [
  { key: 'coach', to: '/coach', requiredRoles: null },
  { key: 'dashboard', to: '/dashboard', requiredRoles: null },
  { key: 'frameworks', to: '/frameworks', requiredRoles: null },
  { key: 'notifications', to: '/notifications', requiredRoles: null },
  // Adoption is tenant_admin-only (V1.x tenant_member won't see it).
  { key: 'adoption', to: '/adoption', requiredRoles: ['tenant_admin'] },
];

/**
 * Filter the nav model to what a given role set is permitted to see.
 *
 * Convenience filter only — the backend authorizes every request regardless
 * (per supplement §3.D RBAC posture). Hiding nav avoids dead links; a
 * forbidden deep-link hits the shell 403 state.
 */
export function visibleNav(
  roles: ReadonlyArray<ConsumerRoleKey>,
): NavItem[] {
  return NAV_MODEL.filter(
    (item) =>
      item.requiredRoles === null ||
      item.requiredRoles.some((r) => roles.includes(r)),
  );
}

/** Resolve a route's NavItemKey by pathname — for active state highlighting. */
export function navItemKeyForPath(pathname: string): NavItemKey | undefined {
  // Exact and prefix matches; longest-prefix wins to handle deep-links like
  // /frameworks/:id/activities/:aid → still highlights 'frameworks'.
  const sorted = [...NAV_MODEL].sort((a, b) => b.to.length - a.to.length);
  const match = sorted.find(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  return match?.key;
}
